import { create } from 'zustand';

/**
 * Asking for an action on a task from somewhere that cannot perform it
 * (spec 125 research R4).
 *
 * The command palette lives in the shell and knows only the open task's id;
 * the board's keyboard handler knows only the focused card's. Whoever holds
 * the task's full context — the open drawer, or the board canvas — takes the
 * request and runs it through the same controls a click would. This store
 * carries intent only, never server data.
 */
export type TaskCommand =
  | 'stop'
  | 'run-again'
  | 'hide'
  | 'copy-link'
  // Start every created task the open join waits on (spec 140 US2).
  | 'start-children'
  /*
    The next action, named in full (feedback 2026-10-06). There used to be one
    `'primary'` command, which pressed the drawer header's next-action button;
    that button is gone, so whoever asks says which step to start at or which
    way on to take, and the drawer runs it without a control to press.
  */
  | { kind: 'start'; stepId: string }
  | { kind: 'decide'; to: string };

interface TaskCommandState {
  pending: { taskId: string; command: TaskCommand; nonce: number } | null;
  request: (taskId: string, command: TaskCommand) => void;
  /** Take the request for `taskId`, if there is one; it is gone afterwards. */
  take: (taskId: string) => TaskCommand | null;
}

export const useTaskCommandChannel = create<TaskCommandState>((set, get) => ({
  pending: null,
  request: (taskId, command) => {
    set({ pending: { taskId, command, nonce: Date.now() } });
  },
  take: (taskId) => {
    const pending = get().pending;
    if (!pending || pending.taskId !== taskId) return null;
    set({ pending: null });
    return pending.command;
  },
}));
